import { create } from "zustand";
import api from "../lib/api";
import type { AxiosError } from "axios";
import { useThemeStore, type ThemeMode } from "./themeStore";

export interface UserPreferences {
  theme?: ThemeMode;
  // Free-form: backend stores any extra keys as-is so we can extend without
  // schema churn (language, density, default landing page, ...).
  [key: string]: unknown;
}

export interface User {
  id: string;
  email: string;
  full_name: string;
  phone?: string;
  is_active: boolean;
  is_superuser: boolean;
  is_email_verified: boolean;
  is_phone_verified: boolean;
  auth_method?: string;
  sso_provider?: string;
  preferences?: UserPreferences;
  created_at: string;
  updated_at: string;
}

/** Apply the theme from a freshly-loaded user record, if one is set. */
function syncThemeFromUser(user: User | null | undefined) {
  const themePref = user?.preferences?.theme;
  if (themePref) {
    useThemeStore.getState().hydrateFromServer(themePref);
  }
}

export interface UserRole {
  role: string;
  virtual_site_id?: string;
  virtual_site_name?: string;
}

export interface VirtualSite {
  id: string;
  name: string;
  code: string;
  location?: string;
  is_active: boolean;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  roles: UserRole[];
  /** Backend returns `virtual_sites`; we normalize to support both field names */
  virtual_sites: VirtualSite[];
  /** @deprecated Use virtual_sites. Kept for backward compat with older API responses. */
  sites?: VirtualSite[];
}

export interface SSOProvider {
  provider: string;
  organization_id: string;
  organization_name: string;
}

/** Normalize org data so virtual_sites is always populated regardless of API field name.
 *
 * The backend's OrgRoleInfo carries a singular org-level `role` plus a
 * `virtual_sites: [{id, name, role}]` list of site-scoped roles. The UI expects
 * a `roles: UserRole[]` array, so we flatten both shapes into that. Without
 * this, `canAdminOrg` always sees an empty roles array and org_admin /
 * site_admin users incorrectly lose write access (Upload buttons, etc.). */
function normalizeOrganizations(orgs: unknown): Organization[] {
  if (!Array.isArray(orgs)) return [];
  return orgs.map((o: Record<string, unknown>) => {
    const rawSites = (o.virtual_sites || o.sites || []) as Array<
      Record<string, unknown>
    >;
    const roles: UserRole[] = [];
    if (typeof o.role === "string") {
      roles.push({ role: o.role });
    }
    for (const s of rawSites) {
      if (typeof s.role === "string") {
        roles.push({
          role: s.role,
          virtual_site_id: s.id as string | undefined,
          virtual_site_name: s.name as string | undefined,
        });
      }
    }
    // If the backend ever sends an explicit `roles` array, prefer it.
    const explicitRoles = Array.isArray(o.roles) ? (o.roles as UserRole[]) : null;
    return {
      ...o,
      virtual_sites: rawSites as unknown as VirtualSite[],
      roles: explicitRoles ?? roles,
    };
  }) as Organization[];
}

interface ApiErrorResponse {
  error: string;
  detail: string;
}

interface AuthState {
  user: User | null;
  organizations: Organization[];
  ssoProviders: SSOProvider[];
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    fullName: string
  ) => Promise<{ userId: string; message: string }>;
  verifyEmail: (email: string, code: string) => Promise<void>;
  logout: () => void;
  fetchMe: () => Promise<void>;
  fetchOrganizations: () => Promise<void>;
  fetchSSOProviders: () => Promise<void>;
  setTokens: (accessToken: string, refreshToken: string) => void;
  clearError: () => void;
  initializeAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  organizations: [],
  ssoProviders: [],
  isAuthenticated: !!localStorage.getItem("access_token"),
  isLoading: !!localStorage.getItem("access_token"),
  error: null,

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post("/auth/login", null, {
        params: { email, password },
      });
      const { access_token, refresh_token, user, organizations } =
        response.data;
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      set({
        user,
        organizations: normalizeOrganizations(organizations),
        isAuthenticated: true,
        isLoading: false,
      });
      syncThemeFromUser(user);
    } catch (err) {
      const axiosErr = err as AxiosError<ApiErrorResponse>;
      const message =
        axiosErr.response?.data?.detail || "Login failed. Please try again.";
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  register: async (email: string, password: string, fullName: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post("/auth/register", null, {
        params: { email, password, full_name: fullName },
      });
      set({ isLoading: false });
      return {
        userId: response.data.user_id,
        message: response.data.message,
      };
    } catch (err) {
      const axiosErr = err as AxiosError<ApiErrorResponse>;
      const message =
        axiosErr.response?.data?.detail ||
        "Registration failed. Please try again.";
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  verifyEmail: async (email: string, code: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post("/auth/verify-email", null, {
        params: { email, code },
      });
      const { access_token, refresh_token, user, organizations } =
        response.data;
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      set({
        user,
        organizations: normalizeOrganizations(organizations),
        isAuthenticated: true,
        isLoading: false,
      });
      syncThemeFromUser(user);
    } catch (err) {
      const axiosErr = err as AxiosError<ApiErrorResponse>;
      const message =
        axiosErr.response?.data?.detail ||
        "Verification failed. Please try again.";
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  logout: () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    set({
      user: null,
      organizations: [],
      isAuthenticated: false,
      error: null,
    });
  },

  fetchMe: async () => {
    try {
      const response = await api.get("/auth/me");
      set({ user: response.data });
      syncThemeFromUser(response.data);
    } catch {
      if (get().isAuthenticated) {
        get().logout();
      }
    }
  },

  fetchOrganizations: async () => {
    try {
      const response = await api.get("/auth/me/organizations");
      set({ organizations: normalizeOrganizations(response.data) });
    } catch {
      /* silently fail */
    }
  },

  fetchSSOProviders: async () => {
    try {
      const response = await api.get("/sso/providers");
      set({ ssoProviders: response.data });
    } catch {
      /* SSO may not be configured */
    }
  },

  setTokens: (accessToken: string, refreshToken: string) => {
    localStorage.setItem("access_token", accessToken);
    localStorage.setItem("refresh_token", refreshToken);
    set({ isAuthenticated: true });
  },

  clearError: () => set({ error: null }),

  initializeAuth: async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      set({ isAuthenticated: false, isLoading: false });
      return;
    }
    set({ isLoading: true });
    try {
      const [meRes, orgsRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/auth/me/organizations"),
      ]);
      set({
        user: meRes.data,
        organizations: normalizeOrganizations(orgsRes.data),
        isAuthenticated: true,
        isLoading: false,
      });
      syncThemeFromUser(meRes.data);
    } catch {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      set({ isAuthenticated: false, isLoading: false });
    }
  },
}));
